import React, { useState } from 'react'
import { ATLButton, ButtonTypeEnum } from '@appsimples/atlantica-react-native'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import styled from 'styled-components/native'
import { icons, images } from '../../constants/searchAssets'
import { ATLTextInputField } from '../../components/form/ATLTextInputField'
import { atlTheme } from '../../atlTheme'
import { useForm } from 'react-simple-hooks-forms/dist'
import {
  buildValidatorFromConstraints,
  buildValidatorFromConstraintsV2
} from '../../utils/buildValidatorFromConstraints'
import { BaseNavigationProps } from '../../navigation/_root'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { CPF_LENGTH } from '../../constants/lengths'
import { ATLTextInputDocumentMaskField } from '../../components/form/ATLTextInputCPFMaskField'
import { WHATSAPP_WEB_URL } from '../../constants/whatsAppInfo'
import { loginButtonRadius } from '../../constants/dimensions'
import { signIn } from '../../services/auth/signIn'
import { onlyNumberString } from '../../utils/formatters'
import { usePromise } from '../../utils/hooks/usePromise'
import { Keyboard, TouchableOpacity } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { syncUser } from '../../services/main/syncUser'
import { quarterOfWindowWidth } from '../../constants/quarterOfWindowWidth'
import '../../index.css'
import {
  SubContainer,
  LoginFormContainer,
  constraints,
  formName,
  graphQLErrorsCode,
  extraScrollHeight,
  formFields,
  SignUpLabel,
  SignUpActionLabel,
} from './common'
import { openWhatsAppWeb } from '../../utils/openWhatsAppWeb'
import { LoadingButton } from '../../components/loading-button/LoadingButton'
import { setGlobal } from '../../global/useGlobal.web'
import { SnackbarWeb, TYPES_SEVERITY } from '../../components/snackbar/web/Snackbar'
import { useForm as useFormV2 } from 'react-hook-form'
import { FormProvider } from 'react-hook-form'
import { showErrorSnackbar } from '../../components/snackbar/Snackbar'

const LogoWebContainer = styled.Image`
  align-self: center;
  width: 25%;
  flex: 1;
`

const FormsContainer = styled.View`
  flex: 1;
  flex-direction: column;
  min-height: fit-content;
`

const SignUpContainer = styled.View`
  width: 100%;
  padding: 0px ${atlTheme.SpacingMarginal}px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  background: ${atlTheme.ColorSurface};
  padding-bottom: 16px;
`

export const LoginScreen: NavigationStackScreenComponent<BaseNavigationProps> = ({ }) => {
  const formMethods = useFormV2({ resolver: buildValidatorFromConstraintsV2(constraints) })
  const [textSnackbar, setTextSnackbar] = useState<string>()
  const [openSnackbar, setOpenSnackbar] = useState(false)

  const { loading: isLoginLoading, request: doLogin } = usePromise(signIn, [], {
    loadingImmediately: false,
    onSuccess: data => onSuccessfulSignIn(data),
    onError: err => onError(err),
  })

  const onSuccessfulSignIn = async data => {
    if (data[0]?.signIn?.isFirstAccess) {
      await setGlobal({ passwordOnFirstLogin: data[1] })
      await Coordinator.goToFirstLogin()
    } else {
      await Coordinator.goToMainScreen()
    }
    // await syncUser()
  }

  const onError = err => {
    if (err?.graphQLErrors[0]?.extensions.code === graphQLErrorsCode.USER_NOT_FOUND) {
      showErrorSnackbar(formatMessage(messages.loginScreen_snackbar_invalidCPF))
    } else if (err.graphQLErrors[0]?.extensions.code === graphQLErrorsCode.INVALID_PASSWORD) {
      showErrorSnackbar(formatMessage(messages.loginScreen_snackbar_invalidPassword))
    }
    showErrorSnackbar(formatMessage(messages.loginScreen_snackbar_unknownError))
  }

  const onCloseSnackbar = () => {
    setOpenSnackbar(false)
  }

  const onSuccessSubmit = async (val: any) => {
    doLogin(
      {
        cpf: onlyNumberString(val.cpf),
        password: val.password,
      },
      val.password
    )
  }

  const onErrorSubmit = (err: any) => {
    console.log('Error while validating login and password: ', err)
  }

  const onSubmit = async () => {
    formMethods.handleSubmit(onSuccessSubmit, onErrorSubmit)()
    Keyboard.dismiss()
  }

  const onPressRecoverPassword = () => Coordinator.goToPasswordRecovery()

  const onPressContactUs = () => {
    openWhatsAppWeb({ url: WHATSAPP_WEB_URL })
  }

  return (
    <LoginFormContainer>
      <KeyboardAwareScrollView
        extraScrollHeight={extraScrollHeight}
        contentContainerStyle={{ flex: 1 }}
        enableOnAndroid
        scrollEnabled={false}
        enableAutomaticScroll
      >
        <SubContainer>
          <LogoWebContainer source={images.mainLogo} resizeMode={'contain'} />
          <FormsContainer>
            <FormProvider {...formMethods}>
              <ATLTextInputDocumentMaskField
                verticalMargin={atlTheme.SpacingMarginal}
                name={formFields.CPF}
                title={formatMessage(messages.loginScreen_cpf)}
                keyboardType={undefined}
                leftIcon={icons.avatarLogin}
                maxLength={CPF_LENGTH}
                formVersion={'2'}
              />
              <ATLTextInputField
                horizontalMargin={quarterOfWindowWidth}
                name={formFields.PASSWORD}
                title={formatMessage(messages.loginScreen_password)}
                hideClearButton
                leftIcon={icons.lock}
                showVisibilityToggle
                textInputBaseProps={{
                  focusColor: atlTheme.ColorMediumContrast,
                  leftIconColor: atlTheme.ColorBrand2Default,
                }}
                formVersion={'2'}
              />
            </FormProvider>
            <LoadingButton
              title={formatMessage(messages.loginScreen_enter)}
              isLoading={isLoginLoading}
              onSubmit={onSubmit}
              horizontalMargin={quarterOfWindowWidth}
            />
            <ATLButton
              verticalMargin={atlTheme.SpacingMarginal}
              type={ButtonTypeEnum.outlined}
              title={formatMessage(messages.loginScreen_forgotMyPassword)}
              buttonBaseProps={{ textColorComplementary: atlTheme.ColorBrand1Default, borderRadius: loginButtonRadius }}
              onPress={onPressRecoverPassword}
              horizontalMargin={quarterOfWindowWidth}
            />
            <ATLButton
              horizontalMargin={atlTheme.SpacingDistribute}
              type={ButtonTypeEnum.label}
              title={formatMessage(messages.loginScreen_contactUS)}
              buttonBaseProps={{
                textColorComplementary: atlTheme.ColorBrand2Default,
                borderRadius: loginButtonRadius,
                borderColor: atlTheme.ColorBrand1Default,
              }}
              onPress={onPressContactUs}
            />
            <SignUpContainer>
              <SignUpLabel>{formatMessage(messages.signUp_notRegisteredYet)}</SignUpLabel>
              <TouchableOpacity onPress={Coordinator.goToSignUp}>
                <SignUpActionLabel>{formatMessage(messages.signUp_actionLabel)}</SignUpActionLabel>
              </TouchableOpacity>
            </SignUpContainer>
          </FormsContainer>
        </SubContainer>
      </KeyboardAwareScrollView>
      <SnackbarWeb
        open={openSnackbar}
        onClose={onCloseSnackbar}
        message={textSnackbar}
        severity={TYPES_SEVERITY.error}
      />
    </LoginFormContainer>
  )
}

LoginScreen.navigationOptions = {
  headerShown: false,
}
